import React, { useState, useEffect, useRef } from 'react';
import './GlobalApplicantSearch.css';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';

const GlobalApplicantSearch = ({ isOpen, onClose, applicants = [], onSelectApplicant }) => {
  const { t } = useTranslation('applicants');
  const { isRTL } = useLanguage();
  const [query, setQuery] = useState('');
  const [filtered, setFiltered] = useState([]);
  const inputRef = useRef(null);

  // Focus the input whenever the modal opens
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setFiltered([]);
      // Give the modal a tick to render before focusing
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 50);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [isOpen, onClose]);

  // Filter applicants whenever the query changes
  useEffect(() => {
    if (!query.trim()) {
      setFiltered([]);
      return;
    }

    const lower = query.toLowerCase();
    const results = applicants.filter((a) =>
      a.name.toLowerCase().includes(lower) ||
      (a.jobTitle && a.jobTitle.toLowerCase().includes(lower))
    );
    setFiltered(results);
  }, [query, applicants]);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    // Close when clicking on the semi-transparent background
    if (e.target.classList.contains('global-search-overlay')) {
      onClose();
    }
  };

  const handleSelect = (applicant) => {
    if (onSelectApplicant) {
      onSelectApplicant(applicant);
    }
  };

  return (
    <div className="global-search-overlay" onClick={handleOverlayClick} style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
      <div className="global-search-modal" role="dialog" aria-modal="true" aria-label={t('globalSearch.label')}>
        <div className="gas-search-row">
          <i className="fa-solid fa-magnifying-glass gas-search-icon" aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            className="gas-search-input"
            placeholder={t('searchPlaceholder')}
            aria-label={t('globalSearch.label')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{ fontFamily: isRTL ? 'Alexandria, Montserrat' : 'Montserrat' }}
          />
          {query && <button type="button" className="gas-clear" onClick={() => setQuery('')} aria-label={t('globalSearch.clear')}><i className="fa-solid fa-xmark" aria-hidden="true" /></button>}
          <button type="button" className="gas-close" onClick={onClose} aria-label={t('globalSearch.close')}><i className="fa-solid fa-xmark" aria-hidden="true" /></button>
        </div>
        <div className="gas-results" aria-live="polite">
          {!query.trim() && <div className="gas-empty"><i className="fa-solid fa-users" aria-hidden="true" /><span>{t('globalSearch.hint')}</span></div>}
          {query.trim() && filtered.length > 0 && <p className="gas-result-count">{t('globalSearch.results', { count: filtered.length })}</p>}
          {filtered.map((app) => (
            <button type="button" key={app.id} className="gas-result" onClick={() => handleSelect(app)}>
              <span className="gas-avatar" aria-hidden="true">{String(app.name || '?').trim().charAt(0)}</span>
              <span className="gas-result-copy"><strong>{app.name}</strong>{app.jobTitle && <span>{app.jobTitle}</span>}</span>
              <i className={`fa-solid ${isRTL ? 'fa-arrow-left' : 'fa-arrow-right'} gas-result-arrow`} aria-hidden="true" />
            </button>
          ))}
          {query.trim() && filtered.length === 0 && (
            <div className="gas-empty"><i className="fa-solid fa-magnifying-glass" aria-hidden="true" /><strong>{t('globalSearch.noResults')}</strong><span>{t('globalSearch.tryAgain')}</span></div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GlobalApplicantSearch; 
